import { useDriverOrderCompleteness } from "../../components/DriverOrders/useDriverOrderCompleteness";
/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Order } from "../../models";
import { useGlobalOrders, useAllOrdersFilters } from "../../hooks";
import { useWorkspaceData } from "@data";
import { useWorkspace } from "../../context/WorkspaceContext";
import { Toast } from "flowbite-react";
import { HiOutlineX, HiCheck, HiOutlineFilter } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import {
  EmptyState,
  emptyOrdersIllustration,
  OrderSuccessPopup,
  OrderList,
  MultiSelectFilterOption,
  SingleSelectFilterOption,
} from "@components";
import { OrderStatus } from "../../types/order/IOrder";
import { buildShareableUrl } from "../../helpers/shareableUrl";
import { selectOrderListSource } from "../../helpers/orderListSource";
import { AllOrdersPageHeader } from "./components/AllOrdersPageHeader";
import { AllOrdersFilterBar } from "./components/AllOrdersFilterBar";

export const AllOrdersPage: FC = function () {
  const [searchParams, setSearchParams] = useSearchParams();
  const [missingOnly, setMissingOnly] = useState(
    searchParams.get("missing") === "1",
  );
  const [splitIsOpen, setSplitIsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | undefined>(
    undefined,
  );
  const { activeWorkspace } = useWorkspace();
  const { t } = useTranslation(["common", "orders", "validation", "stats"]);
  const navigate = useNavigate();
  const location = useLocation();

  // Sjekk om vi har en from-state for tilbakeknappen
  const fromPath = location.state?.from;

  // Funksjon for å navigere tilbake
  const handleGoBack = () => {
    if (fromPath) {
      // Naviger tilbake til den opprinnelige stien
      navigate(fromPath);
    } else if (location.state?.driverId) {
      // Hvis vi har en driverId i state, naviger til sjåførdetaljene
      navigate(
        `/resources/drivers?driver=${encodeURIComponent(location.state.driverId)}`,
      );
    } else {
      // Fallback hvis fromPath ikke er definert
      navigate("/resources/drivers");
    }
  };

  // Filter state + URL/debounce/isFiltering lifecycle
  const {
    statusFilter,
    setStatusFilter,
    driverFilter,
    setDriverFilter,
    customerSearchText,
    setCustomerSearchText,
    debouncedCustomerSearchText,
    isFiltering,
    setIsFiltering,
    clearFilters,
    statusFilterCount,
    driverFilterActive,
    hasActiveFilters,
  } = useAllOrdersFilters(searchParams, setSearchParams);

  // Dropdown states to control opening/closing
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);

  // Refs for dropdown elements
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const driverDropdownRef = useRef<HTMLDivElement>(null);

  // Drivers for the filter dropdown come from the centrally-loaded store.
  const { drivers, loading } = useWorkspaceData();

  // Filter options
  const statusOptions = [
    OrderStatus.New,
    OrderStatus.Scheduled,
    OrderStatus.InProgress,
    OrderStatus.Cancelled,
    OrderStatus.Completed,
  ];

  // Lukk dropdown-menyer når språket endres for å forhindre ResizeObserver-feil
  useEffect(() => {
    // Når t-funksjonen endres (språket endres), lukk alle dropdown-menyer
    setStatusDropdownOpen(false);
    setDriverDropdownOpen(false);
  }, [t]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Close status dropdown if click is outside
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node) &&
        statusDropdownOpen
      ) {
        setStatusDropdownOpen(false);
      }

      // Close driver dropdown if click is outside
      if (
        driverDropdownRef.current &&
        !driverDropdownRef.current.contains(event.target as Node) &&
        driverDropdownOpen
      ) {
        setDriverDropdownOpen(false);
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [statusDropdownOpen, driverDropdownOpen]);

  const workspaceId = activeWorkspace?.workspaceId ?? "";
  // Already shared by the planner, details and completeness checks. Keep one
  // live source instead of combining it with a separately cached API page.
  const allOrdersQuery = useGlobalOrders();
  const allOrders = useMemo(
    () => allOrdersQuery.data ?? [],
    [allOrdersQuery.data],
  ) as Order[];

  // Details opened by URL must not depend on the current server page or filters.
  // Reuse the workspace listener rather than loading pages until the row appears.
  const linkedOrderId = searchParams.get("id");
  const linkedOrder = allOrders.find(
    (order) => order.id === linkedOrderId && order.workspaceId === workspaceId,
  );

  const completeness = useDriverOrderCompleteness(
    allOrders.map((order) => order.id),
  );
  const missingCount = Object.values(completeness.data ?? {}).filter(
    (value) => value.needsAttention,
  ).length;

  const matchingOrders = useMemo(
    () =>
      selectOrderListSource(allOrders, {
        workspaceId,
        statuses: statusFilter,
        driverId: driverFilter,
        search: debouncedCustomerSearchText,
        missingOnly,
        completeness: completeness.data,
      }),
    [
      allOrders,
      workspaceId,
      statusFilter,
      driverFilter,
      debouncedCustomerSearchText,
      missingOnly,
      completeness.data,
    ],
  );
  const [visibleCount, setVisibleCount] = useState(20);
  useEffect(() => {
    setVisibleCount(20);
  }, [
    workspaceId,
    statusFilter,
    driverFilter,
    debouncedCustomerSearchText,
    missingOnly,
  ]);
  const orders = useMemo(
    () => matchingOrders.slice(0, visibleCount),
    [matchingOrders, visibleCount],
  );
  const hasMoreOrders = matchingOrders.length > visibleCount;
  const isQueryLoading = Boolean(
    (missingOnly && completeness.isLoading) ||
    loading.drivers ||
    allOrdersQuery.isLoading,
  );

  // Reset isFiltering flag when query is done loading
  useEffect(() => {
    if (!isQueryLoading && isFiltering) {
      // Øk forsinkelsen for å sikre at filtreringen er fullført
      // og at tabellen ikke åpner seg for tidlig
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 1000); // Økt fra 500ms til 1000ms

      return () => clearTimeout(timer);
    }
  }, [isQueryLoading, isFiltering, searchParams, setIsFiltering]);

  const hasOrders = orders.length > 0;
  const hasAnyOrders = allOrders.length > 0;

  // Handle status filter change
  const handleStatusFilterChange = (
    status: OrderStatus,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation(); // Prevent click from propagating
    setIsFiltering(true); // Set filtering flag

    // Update status filter
    let newStatusFilter: OrderStatus[];
    if (statusFilter.includes(status)) {
      newStatusFilter = statusFilter.filter((s) => s !== status);
    } else {
      newStatusFilter = [...statusFilter, status];
    }

    setStatusFilter(newStatusFilter);
  };

  // Handle driver filter change
  const handleDriverFilterChange = (driverId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating
    setIsFiltering(true); // Set filtering flag

    // Hvis samme driver er valgt, fjern filteret (toggle off)
    // Ellers, sett filteret til den valgte driveren (single select)
    if (driverFilter === driverId) {
      setDriverFilter(undefined);
    } else {
      setDriverFilter(driverId);
    }

    // Lukk dropdown etter valg
    setDriverDropdownOpen(false);
  };

  // Handle customer search - update immediately for responsive UI
  const handleCustomerSearch = (searchText: string) => {
    setCustomerSearchText(searchText);
  };

  // Custom dropdown toggle handlers med debounce for å forhindre ResizeObserver-feil
  const toggleStatusDropdown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating to elements behind
    // Bruk setTimeout for å forhindre at ResizeObserver-hendelser stables opp
    setTimeout(() => {
      setStatusDropdownOpen(!statusDropdownOpen);
      if (!statusDropdownOpen) {
        setDriverDropdownOpen(false); // Close other dropdown when opening this one
      }
    }, 10);
  };

  const toggleDriverDropdown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating to elements behind
    // Bruk setTimeout for å forhindre at ResizeObserver-hendelser stables opp
    setTimeout(() => {
      setDriverDropdownOpen(!driverDropdownOpen);
      if (!driverDropdownOpen) {
        setStatusDropdownOpen(false); // Close other dropdown when opening this one
      }
    }, 10);
  };

  // Function to generate a shareable link with current filters
  const getShareableLink = () =>
    buildShareableUrl([
      ["id", searchParams.get("id")],
      ["missing", missingOnly ? "1" : undefined],
      ["status", statusFilter.length > 0 ? statusFilter.join(",") : undefined],
      ["driver", driverFilter],
      ["customer", customerSearchText || undefined],
    ]);

  // State for toast notification
  const [showLinkCopiedToast, setShowLinkCopiedToast] = useState(false);

  // State for order confirmation popup
  const [showOrderConfirmedPopup, setShowOrderConfirmedPopup] = useState(false);
  const [confirmedOrderId, setConfirmedOrderId] = useState<string | null>(null);

  // Check if we came from order confirmation
  useEffect(() => {
    const isConfirmed = searchParams.get("confirmed") === "true";
    const orderId = searchParams.get("id");

    if (isConfirmed && orderId) {
      setConfirmedOrderId(orderId);
      setShowOrderConfirmedPopup(true);

      // Remove the confirmed parameter from URL after showing popup
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("confirmed");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle planner navigation
  const handlePlanOrder = () => {
    if (confirmedOrderId) {
      // Find the confirmed order to get driver and date info
      const confirmedOrder = allOrders.find(
        (order) => order.id === confirmedOrderId,
      );

      if (confirmedOrder && confirmedOrder.driverId) {
        // Get driver email
        const driverEmail = confirmedOrder.driverId;

        // Get route date from the first route item
        const routeDate = confirmedOrder.route?.[0]?.stopDate;
        let dateString = new Date().toISOString().split("T")[0]; // Default to today

        if (routeDate) {
          // Convert Firestore timestamp to date string
          const date = routeDate.toDate
            ? routeDate.toDate()
            : new Date(routeDate as any);
          dateString = date.toISOString().split("T")[0];
        }

        // Navigate to planner with specific driver and date
        const plannerUrl = `/planner?tab=route&date=${dateString}&driver=${encodeURIComponent(driverEmail)}`;
        navigate(plannerUrl);
      } else {
        // Fallback if order not found or no driver - just go to planner
        navigate("/planner");
      }
    }
    setShowOrderConfirmedPopup(false);
  };

  const handleOpenDailyPlanner = () => {
    const confirmedOrder = allOrders.find(
      (order) => order.id === confirmedOrderId,
    );
    const routeDate = confirmedOrder?.route?.[0]?.stopDate;
    const date = routeDate?.toDate
      ? routeDate.toDate()
      : routeDate
        ? new Date(routeDate as any)
        : new Date();
    const params = new URLSearchParams({
      date: [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-"),
    });
    if (confirmedOrderId) params.set("orderId", confirmedOrderId);
    navigate(`/daily-planner?${params.toString()}`);
    setShowOrderConfirmedPopup(false);
  };

  // Handle creating new order
  const handleCreateNewOrder = () => {
    navigate("/orders/new");
    setShowOrderConfirmedPopup(false);
  };

  // Handle closing the success popup
  const handleCloseOrderConfirmPopup = () => {
    setShowOrderConfirmedPopup(false);
    setConfirmedOrderId(null);
  };

  // Function to copy shareable link to clipboard
  const copyShareableLink = () => {
    const link = getShareableLink();
    navigator.clipboard
      .writeText(link)
      .then(() => {
        // Show toast notification
        setShowLinkCopiedToast(true);
        // Hide toast after 3 seconds
        setTimeout(() => setShowLinkCopiedToast(false), 3000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  const statusFilterOptions: MultiSelectFilterOption[] = statusOptions.map(
    (status) => ({
      key: status,
      selected: statusFilter.includes(status),
      label: (
        <>
          {status === OrderStatus.Draft && t("stats:order_status.draft")}
          {status === OrderStatus.New && t("stats:order_status.new")}
          {status === OrderStatus.Scheduled &&
            t("stats:order_status.scheduled")}
          {status === OrderStatus.InProgress &&
            t("stats:order_status.in_progress")}
          {status === OrderStatus.Cancelled &&
            t("stats:order_status.cancelled")}
          {status === OrderStatus.Completed &&
            t("stats:order_status.completed")}
        </>
      ),
    }),
  );

  const driverFilterOptions: SingleSelectFilterOption[] = drivers.map(
    (driver) => ({
      key: driver.email,
      label: `${driver.firstName} ${driver.lastName}`,
    }),
  );

  return (
    <>
      {/* Order Success Popup */}
      {showOrderConfirmedPopup && (
        <OrderSuccessPopup
          onClose={handleCloseOrderConfirmPopup}
          onPlanStops={handlePlanOrder}
          onOpenDailyPlanner={handleOpenDailyPlanner}
          onCreateNewOrder={handleCreateNewOrder}
          orderId={confirmedOrderId || undefined}
        />
      )}

      {/* Toast notification for link copied */}
      {showLinkCopiedToast && (
        <div className="fixed bottom-5 right-5 z-50">
          <Toast>
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200">
              <HiCheck className="h-5 w-5" />
            </div>
            <div className="ml-3 text-sm font-normal">
              {t("common:link_copied")}
            </div>
            <button
              type="button"
              className="ml-auto -mx-1.5 -my-1.5 bg-white text-muted hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
              onClick={() => setShowLinkCopiedToast(false)}
              aria-label={t("common:close")}
            >
              <span className="sr-only">{t("common:close")}</span>
              <HiOutlineX className="w-5 h-5" />
            </button>
          </Toast>
        </div>
      )}

      {/* Always show the header and filters if there are any orders in the workspace */}
      {(hasOrders || (hasAnyOrders && !isQueryLoading)) && (
        <div className="flex flex-col px-4 py-2">
          <AllOrdersPageHeader
            splitIsOpen={splitIsOpen}
            fromPath={fromPath}
            onGoBack={handleGoBack}
          />

          <AllOrdersFilterBar
            hasSelectedOrder={!!selectedOrder}
            splitIsOpen={splitIsOpen}
            customerSearchText={customerSearchText}
            onCustomerSearch={handleCustomerSearch}
            statusDropdownRef={statusDropdownRef}
            statusDropdownOpen={statusDropdownOpen}
            onToggleStatusDropdown={toggleStatusDropdown}
            statusOptions={statusFilterOptions}
            onStatusOptionClick={(key, e) =>
              handleStatusFilterChange(key as OrderStatus, e)
            }
            onClearStatus={() => setStatusFilter([])}
            statusFilterCount={statusFilterCount}
            missingInfo={{
              visible:
                missingOnly ||
                allOrders.some((order) => order.id.startsWith("drv_")),
              active: missingOnly,
              count: missingCount,
              disabled:
                !missingOnly &&
                (completeness.isLoading || completeness.isError),
              onToggle: () => {
                setMissingOnly((active) => !active);
                setStatusDropdownOpen(false);
                setDriverDropdownOpen(false);
              },
            }}
            driverDropdownRef={driverDropdownRef}
            driverDropdownOpen={driverDropdownOpen}
            onToggleDriverDropdown={toggleDriverDropdown}
            driverOptions={driverFilterOptions}
            driverFilter={driverFilter}
            onDriverOptionClick={(key, e) => handleDriverFilterChange(key, e)}
            onClearDriver={() => setDriverFilter(undefined)}
            showActions={
              missingOnly ||
              statusFilterCount > 0 ||
              driverFilterActive > 0 ||
              customerSearchText.length > 0
            }
            onClearFilters={() => {
              clearFilters();
              setMissingOnly(false);
            }}
            onCopyShareableLink={copyShareableLink}
            onCreateNewOrder={() => navigate("/orders/new")}
          />
          {completeness.isError && (
            <button
              type="button"
              className="self-start mb-2 text-sm text-brand underline"
              onClick={() => completeness.refetch()}
            >
              {t("orders:driverOrder.checkFailed")}
            </button>
          )}
        </div>
      )}

      {allOrdersQuery.isError && (
        <p role="alert" className="mx-4 mb-3 text-sm text-danger">
          {t("orders:list_refresh_failed")}
        </p>
      )}
      {/* An unavailable snapshot is not evidence that the workspace is empty. */}
      {!allOrdersQuery.isError &&
      !isQueryLoading &&
      !hasOrders &&
      !linkedOrder ? (
        hasAnyOrders ? (
          // Empty state for when there are orders in the workspace but none match the current filters
          <EmptyState
            icon={
              <div className="flex justify-center items-center w-20 h-20 rounded-full bg-surface-muted">
                <HiOutlineFilter className="w-10 h-10 text-muted" />
              </div>
            }
            title={t("orders:empty_state.filtered.title")}
            description={t("orders:empty_state.filtered.description")}
            buttonText={t("orders:empty_state.filtered.button_text")}
            buttonClick={() => {
              clearFilters();
              setMissingOnly(false);
            }}
            buttonIcon="x"
            buttonColor="gray"
          />
        ) : (
          // Empty state for when there are no orders at all in the workspace
          <EmptyState
            icon={emptyOrdersIllustration}
            title={t("orders:empty_state.general.title")}
            description={t("orders:empty_state.general.description")}
            buttonText={t("orders:empty_state.general.button_text")}
            buttonClick={() => navigate("/orders/new")}
            buttonIcon="plus"
            buttonColor="primary"
          />
        )
      ) : (
        <div className="px-4">
          <OrderList
            key={workspaceId}
            orders={orders}
            linkedOrder={linkedOrder}
            sortMode="created-desc"
            applyLocalSearch={false}
            isLoading={isQueryLoading}
            isOpen={setSplitIsOpen}
            customerSearchText={customerSearchText}
            onCustomerSearch={handleCustomerSearch}
            onOrderSelect={setSelectedOrder}
            preventAutoOpen={
              (hasActiveFilters || missingOnly || isFiltering) &&
              !searchParams.get("id")
            }
            tableFooter={
              hasMoreOrders && !isQueryLoading ? (
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + 20)}
                  aria-label={t("common:load_more", "Load more")}
                  className="w-full py-3 text-center text-sm font-medium text-brand hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("common:load_more", "Load more")}
                </button>
              ) : undefined
            }
          />
        </div>
      )}
    </>
  );
};
